import { useState, useRef, useEffect, useCallback } from 'react';
import { playerRepository } from '../../data/playerRepository';
import { gerarDeck, gerarDeckSequencial } from '../../domain/usecases/gerarDeck';
import {
  calcularFingerprintPlaylist,
  remapearIndicePorVideo,
} from '../../domain/usecases/playlistFingerprint';
import type { PlaylistItem } from '@/shared/types';

export interface UseYoutubePlaylistOptions {
  /** Chamado quando a playlist é removida no admin (playlist_id passa a null). */
  onPlaylistRemoved?(): void;
  /** Quando false, reproduz na ordem do campo `ordem` (sequencial). */
  reproducaoAleatoria?: boolean;
}

export interface UseYoutubePlaylistReturn {
  playlistItems: PlaylistItem[];
  ytIndex:       number;
  setYtIndex:    React.Dispatch<React.SetStateAction<number>>;
  /** Incrementa a cada avancarDeck — distingue reproduções consecutivas do mesmo índice (playlist de 1 item, deck que recomeça no índice atual). */
  playNonce:     number;
  playlistItemsRef: React.RefObject<PlaylistItem[]>;
  ytIndexRef:       React.RefObject<number>;
  avancarDeck(): number | undefined;
  /** Refetch dos itens (broadcast reload_playlist) sem trocar playlist_id. */
  recarregarItens(): Promise<void>;
}

function indicePlayavel(items: PlaylistItem[], index: number): number {
  const len = items.length;
  if (len === 0) return 0;
  const start = ((index % len) + len) % len;
  for (let i = 0; i < len; i++) {
    const idx = (start + i) % len;
    if (!items[idx]?.erro_embed) return idx;
  }
  return start;
}

function proximoIndicePlayavel(items: PlaylistItem[], atual: number): number | undefined {
  const len = items.length;
  if (len === 0) return undefined;
  const start = ((atual + 1) % len + len) % len;
  for (let i = 0; i < len; i++) {
    const idx = (start + i) % len;
    if (!items[idx]?.erro_embed) return idx;
  }
  return undefined;
}

interface PlaylistStateSalvo {
  deck:        number[];
  ytIndex:     number;
  fingerprint: string;
}

function inicializarDeck(
  playlistId: string,
  data: PlaylistItem[],
  aleatorio: boolean,
): { deckInicial: number[]; indexInicial: number } {
  if (data.length === 0) return { deckInicial: [], indexInicial: 0 };

  if (!aleatorio) {
    const { deck, index } = gerarDeckSequencial(data.length, 0);
    return { deckInicial: deck, indexInicial: index };
  }

  const STORAGE_KEY = `ds:playlist_state_${playlistId}`;
  const fingerprintAtual = calcularFingerprintPlaylist(data);
  const salvo = localStorage.getItem(STORAGE_KEY);

  if (salvo) {
    try {
      const parsed = JSON.parse(salvo) as PlaylistStateSalvo;
      const indexValido = parsed.ytIndex >= 0 && parsed.ytIndex < data.length;
      const deckValido  = Array.isArray(parsed.deck) && parsed.deck.every(n => n >= 0 && n < data.length);
      const fpValido    = parsed.fingerprint === fingerprintAtual;
      if (indexValido && deckValido && fpValido) {
        return {
          deckInicial: parsed.deck,
          indexInicial: indicePlayavel(data, parsed.ytIndex),
        };
      }
      throw new Error('estado inválido');
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  const deck = gerarDeck(data.length);
  return { deckInicial: deck.slice(1), indexInicial: indicePlayavel(data, deck[0]!) };
}

/** Mescla itens novos no deck embaralhado sem interromper o índice atual. */
function mesclarItensPlaylist(
  anterior: PlaylistItem[],
  data: PlaylistItem[],
  deckAtual: number[],
  ytIndexAtual: number,
  aleatorio: boolean,
): { deck: number[]; ytIndex: number } {
  if (data.length === 0) {
    return { deck: [], ytIndex: 0 };
  }

  if (!aleatorio) {
    // Remapeia pelo video_id (como no aleatório): manter o índice numérico
    // cortava a música atual quando um item anterior a ela era removido.
    // Se o vídeo atual saiu da playlist, segue da mesma posição.
    const videoAtual = anterior[ytIndexAtual]?.video_id;
    const remapeado  = videoAtual ? data.findIndex(i => i.video_id === videoAtual) : -1;
    const idx = remapeado >= 0 ? remapeado : (ytIndexAtual < data.length ? ytIndexAtual : 0);
    const { deck, index } = gerarDeckSequencial(data.length, idx);
    return { deck, ytIndex: index };
  }

  const fpAnt = calcularFingerprintPlaylist(anterior);
  const fpNovo = calcularFingerprintPlaylist(data);
  if (fpAnt !== fpNovo && anterior.length > 0) {
    const idx = remapearIndicePorVideo(anterior, data, ytIndexAtual);
    const { deck, index } = reconstruirDeck(data.length, idx, true);
    return { deck, ytIndex: index };
  }

  const oldVideoIds = new Set(anterior.map(i => i.video_id));
  const novosIndices = data
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => !oldVideoIds.has(item.video_id))
    .map(({ idx }) => idx);

  let deck = deckAtual.filter(n => n >= 0 && n < data.length);
  let ytIndex = ytIndexAtual;

  if (ytIndex >= data.length) {
    ytIndex = 0;
  }

  if (novosIndices.length > 0) {
    const embaralhados = gerarDeck(novosIndices.length).map(i => novosIndices[i]!);
    deck = [...deck, ...embaralhados];
  }

  return { deck, ytIndex };
}

function reconstruirDeck(
  len: number,
  indexAtual: number,
  aleatorio: boolean,
): { deck: number[]; index: number } {
  if (len === 0) return { deck: [], index: 0 };
  if (!aleatorio) return gerarDeckSequencial(len, indexAtual);
  // Preserva o índice atual como item corrente e embaralha os demais para o deck.
  // Antes, gerarDeck() sorteava um índice totalmente aleatório, ignorando indexAtual —
  // isso causava skip da música ao recarregar a playlist (ex: aprovar sugestão).
  const restantes = Array.from({ length: len }, (_, i) => i).filter(i => i !== indexAtual);
  const deck = gerarDeck(restantes.length).map(i => restantes[i]!);
  return { deck, index: indexAtual };
}

/** Estado da playlist YouTube: itens, índice atual, deck embaralhado
 *  (Fisher-Yates) e anti-amnésia via localStorage (retoma de onde parou
 *  após reload/queda de luz, chave `ds:playlist_state_${playlistId}`). */
export function useYoutubePlaylist(
  playlistId: string | null | undefined,
  pausarPersistencia: boolean,
  { onPlaylistRemoved, reproducaoAleatoria = true }: UseYoutubePlaylistOptions = {},
): UseYoutubePlaylistReturn {
  const [playlistItems, setPlaylistItems] = useState<PlaylistItem[]>([]);
  const [ytIndex,       setYtIndex]       = useState(0);
  const [playNonce,     setPlayNonce]     = useState(0);
  const deckRef          = useRef<number[]>([]);
  const playlistItemsRef = useRef<PlaylistItem[]>([]);
  const ytIndexRef       = useRef(0);
  const playlistIdAnteriorRef = useRef<string | null>(null);
  const onPlaylistRemovedRef  = useRef(onPlaylistRemoved);
  const reproducaoAleatoriaRef = useRef(reproducaoAleatoria);
  useEffect(() => { onPlaylistRemovedRef.current = onPlaylistRemoved; });
  useEffect(() => { reproducaoAleatoriaRef.current = reproducaoAleatoria; });

  useEffect(() => { playlistItemsRef.current = playlistItems; }, [playlistItems]);
  useEffect(() => { ytIndexRef.current       = ytIndex;       }, [ytIndex]);

  const aplicarItensIniciais = useCallback((id: string, data: PlaylistItem[], aleatorio: boolean) => {
    if (data.length === 0) {
      setPlaylistItems([]);
      setYtIndex(0);
      deckRef.current = [];
      return;
    }
    const { deckInicial, indexInicial } = inicializarDeck(id, data, aleatorio);
    deckRef.current = deckInicial;
    setPlaylistItems(data);
    setYtIndex(indicePlayavel(data, indexInicial));
  }, []);

  const recarregarItens = useCallback(async () => {
    if (!playlistId) return;
    try {
      const data = await playerRepository.listarItensPlaylist(playlistId);
      if (data.length === 0) {
        setPlaylistItems([]);
        setYtIndex(0);
        deckRef.current = [];
        return;
      }

      const anterior = playlistItemsRef.current;
      if (anterior.length === 0) {
        aplicarItensIniciais(playlistId, data, reproducaoAleatoriaRef.current);
        return;
      }

      const { deck, ytIndex: novoIndex } = mesclarItensPlaylist(
        anterior,
        data,
        deckRef.current,
        ytIndexRef.current,
        reproducaoAleatoriaRef.current,
      );
      deckRef.current = deck;
      setPlaylistItems(data);
      setYtIndex(indicePlayavel(data, novoIndex));
    } catch (err) {
      console.warn('[useYoutubePlaylist] Erro ao recarregar playlist:', err);
    }
  }, [playlistId, aplicarItensIniciais]);

  useEffect(() => {
    const anterior = playlistIdAnteriorRef.current;
    playlistIdAnteriorRef.current = playlistId ?? null;

    if (anterior !== null && !playlistId) {
      setPlaylistItems([]);
      setYtIndex(0);
      deckRef.current = [];
      onPlaylistRemovedRef.current?.();
      return;
    }

    if (!playlistId) return;
    // Trocar A→B rápido: a resposta de A chegando depois não pode tocar A.
    let cancelado = false;
    playerRepository.listarItensPlaylist(playlistId)
      .then(data => { if (!cancelado) aplicarItensIniciais(playlistId, data, reproducaoAleatoriaRef.current); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [playlistId, aplicarItensIniciais]);

  // Reconstrói o deck ao alternar sequencial ↔ aleatório sem interromper o índice.
  useEffect(() => {
    const len = playlistItemsRef.current.length;
    if (len === 0) return;
    const { deck, index } = reconstruirDeck(len, ytIndexRef.current, reproducaoAleatoria);
    deckRef.current = deck;
    setYtIndex(index);
  }, [reproducaoAleatoria]);

  useEffect(() => {
    if (!playlistId || pausarPersistencia || !reproducaoAleatoria) return;
    const STORAGE_KEY = `ds:playlist_state_${playlistId}`;
    const fingerprint = calcularFingerprintPlaylist(playlistItemsRef.current);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        deck:        deckRef.current,
        ytIndex:     ytIndex,
        fingerprint,
      } satisfies PlaylistStateSalvo));
    } catch {
      // localStorage cheio (quota exceeded) — não bloqueia a operação
    }
  }, [ytIndex, playlistId, pausarPersistencia, reproducaoAleatoria, playlistItems]);

  const avancarDeck = useCallback(() => {
    const items = playlistItemsRef.current;
    const len = items.length;
    if (len === 0) return undefined;

    if (!reproducaoAleatoriaRef.current) {
      const proximo = proximoIndicePlayavel(items, ytIndexRef.current);
      if (proximo === undefined) return undefined;
      setYtIndex(proximo);
      setPlayNonce(n => n + 1);
      return proximo;
    }

    let proximo: number | undefined;
    const maxTentativas = deckRef.current.length + len;
    for (let t = 0; t < maxTentativas; t++) {
      if (deckRef.current.length === 0) deckRef.current = gerarDeck(len);
      const candidato = deckRef.current.shift()!;
      if (!items[candidato]?.erro_embed) {
        proximo = candidato;
        break;
      }
    }
    if (proximo === undefined) return undefined;
    setYtIndex(proximo);
    setPlayNonce(n => n + 1);
    return proximo;
  }, []);

  return {
    playlistItems, ytIndex, setYtIndex, playNonce, playlistItemsRef, ytIndexRef,
    avancarDeck, recarregarItens,
  };
}
